import { Autocomplete, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { RequireCapability } from '../../auth/RequireAuth';
import { PageHeader } from '../../components/states';
import { fetchTenants, type Tenant } from '../tenants/api';
import { CODE_STATUS_OPTIONS, codeKeys } from './api';
import { CodesPanel, type CodesState } from './CodesPanel';

type TenantOption = Pick<Tenant, 'id' | 'name' | 'slug'>;

/** Shop picker for the tenant filter: searches /platform/tenants as the admin types. */
function TenantFilter({
  value,
  known,
  onChange,
}: {
  value: string | null;
  known: Map<string, TenantOption>;
  onChange: (id: string | null) => void;
}) {
  const [input, setInput] = useState('');
  const search = useQuery({
    queryKey: ['platform', 'tenants', 'picker', input.trim()],
    queryFn: () => fetchTenants({ pageSize: 20, ...(input.trim() ? { q: input.trim() } : {}) }),
    staleTime: 30_000,
  });
  const options: TenantOption[] = search.data?.data ?? [];
  const selected: TenantOption | null = value
    ? (known.get(value) ??
      options.find((o) => o.id === value) ?? { id: value, name: value, slug: '' })
    : null;
  return (
    <Autocomplete<TenantOption>
      value={selected}
      options={
        selected && !options.some((o) => o.id === selected.id) ? [selected, ...options] : options
      }
      loading={search.isFetching}
      filterOptions={(x) => x}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_, v) => onChange(v?.id ?? null)}
      inputValue={input}
      onInputChange={(_, v, reason) => {
        if (reason !== 'reset') setInput(v);
      }}
      renderOption={(props, o) => (
        <li {...props} key={o.id}>
          {o.name}
          {o.slug && (
            <span style={{ opacity: 0.6, marginLeft: 8, fontSize: '0.8em' }}>{o.slug}</span>
          )}
        </li>
      )}
      renderInput={(p) => <TextField {...p} label="Shop" placeholder="All shops" />}
      sx={{ width: { sm: 280 }, flexShrink: 0, ml: { sm: 'auto !important' } }}
    />
  );
}

function CodesList() {
  const [params, setParams] = useSearchParams();
  const statusParam = params.get('status');
  const state: CodesState = {
    status: CODE_STATUS_OPTIONS.find((o) => o.value === statusParam)?.value ?? 'ALL',
    page: Math.max(1, Number(params.get('page')) || 1),
    pageSize: Math.min(100, Math.max(1, Number(params.get('size')) || 25)),
  };
  const tenantId = params.get('tenant');

  // Codes carry only a tenantId: names come from the first 100 shops (newest first).
  const names = useQuery({
    queryKey: codeKeys.tenantNames,
    queryFn: () => fetchTenants({ pageSize: 100 }),
    staleTime: 60_000,
  });
  const known = useMemo(
    () => new Map((names.data?.data ?? []).map((t) => [t.id, t] as const)),
    [names.data],
  );

  const set = useCallback(
    (changes: Record<string, string | null>) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(changes)) {
            if (v === null) next.delete(k);
            else next.set(k, v);
          }
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );

  return (
    <>
      <PageHeader
        eyebrow="Billing"
        title="Activation codes"
        subtitle="Codes shops redeem in the app to extend their subscription. Generate new ones from a shop’s page."
      />
      <CodesPanel
        state={state}
        tenantId={tenantId ?? undefined}
        tenantName={(id) => known.get(id)?.name}
        onStateChange={(next) => {
          const s = { ...state, ...next };
          set({
            status: s.status === 'ALL' ? null : s.status,
            page: s.page === 1 ? null : String(s.page),
            size: s.pageSize === 25 ? null : String(s.pageSize),
          });
        }}
        toolbarExtra={
          <TenantFilter
            value={tenantId}
            known={known}
            onChange={(id) => set({ tenant: id, page: null })}
          />
        }
      />
    </>
  );
}

export function ActivationCodesPage() {
  return (
    <RequireCapability anyOf={['codes.manage']} what="activation codes">
      <CodesList />
    </RequireCapability>
  );
}
